import React, { cloneElement, useRef, useState } from 'react'
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  safePolygon,
  useDismiss,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react'
import theme from '@root/styles/theme'
import styled from 'styled-components'

type HoverChildProps<T extends HTMLElement> = React.HTMLAttributes<T> & React.RefAttributes<T>

interface Props<T extends HTMLElement = HTMLElement> {
  children: React.ReactElement<HoverChildProps<T>>
  content: React.ReactNode
}

export function OnHoverContainer<T extends HTMLElement = HTMLElement>({
  children,
  content
}: Props<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))

  const { context, refs } = useFloating({
    open: isOpen,
    onOpenChange: open => setIsOpen(open),
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 10 }), // Shifts the floating element to keep it in view
      size({
        apply({ availableHeight, elements }) {
          // Set the max height of the floating element to available height
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`
          })
        },
        padding: 10 // Add some padding to the viewport boundary
      })
    ]
  })

  const hover = useHover(context, {
    handleClose: safePolygon()
  })
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss])

  const enhancedChild = cloneElement(children, {
    ref: refs.setReference,
    style: { color: theme.colors.primary },
    ...getReferenceProps()
  })

  return (
    <>
      {enhancedChild}
      {isOpen && (
        <FloatingPortal root={rootRef}>
          <FloatingFocusManager
            context={context}
            returnFocus={false}
            modal={false}
            order={['reference', 'content']}
          >
            <StyledContainer
              ref={refs.setFloating}
              style={{
                top: context.y || 0,
                left: context.x || 0,
                position: context.strategy
              }}
              {...getFloatingProps()}
            >
              {content}
            </StyledContainer>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}

const StyledContainer = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.2);
  max-width: 90dvw;
  max-height: 90dvh;
  overflow: hidden;
`
